import { Spacing } from '@persona-id/design-tokens';
import { ClipboardIcon, CopyIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import { get, intersection, set } from 'lodash/fp';
import { Fragment, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormSection } from 'redux-form';
import styled from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import {
  Asset,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { findComponentConfigPath } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import {
  ComponentAccessTiers,
  COMPONENTS_WITH_ONLY_JSON_LOGIC,
  COMPONENTS_WITHOUT_PROPERTY_SUPPORT,
  RESTRICTED_COMPONENTS,
  ScreenEditorComponentConfigTypes,
  STYLEABLE_COMPONENTS,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-constants';
import getComponentPanelFallback, {
  ComponentPanelFallbackCondition,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-panel-fallbacks';
import { ComponentValidationErrorsMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/component-validations/types';
import ComponentConfigSetting from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ComponentConfigSetting';
import ComponentConfigSettingAddOns from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/ComponentConfigSettingAddOns';
import { ComponentJsonLogicEditor } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor';
import {
  ComponentJsonLogicRecord,
  StepperStepLogicKeys,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import { componentTotalNumConditionalConfigsWithRulesLogic } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import FormComponentFieldsEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/FormComponentFieldsEditor';
import FormComponentOptionalEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/FormComponentOptionalEditor/FormComponentOptionalEditor';
import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import TranslationsEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/TranslationsEditor';
import ValidationsEditor from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ValidationsEditor/ValidationsEditor';
import ComponentEditorStyles from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/ComponentEditorStyleSections';
import {
  COMPONENT_FIELD_DEFAULTS,
  EDITOR_COMPONENTS_SCHEMA,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import useComponentEditorComponentPanelSettings from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/hooks/useComponentEditorComponentPanelSettings';
import {
  PanelHeaderSection,
  PanelHeaderTitle,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/PanelHeaderTitle';
import {
  ComponentSettingImage,
  EditorComponentSchema,
  SelectedComponentAndStep,
  SelectedComponentTab,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import {
  componentSelfServeUiConditions,
  isCurrentComponentBIPASubmitTrigger,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/utils';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import {
  generateUpdatedStepFromComponentUpdates,
  getAssetUpdatedImageConfig,
  getComponentFieldKeys,
  getCurrentComponent,
  getDisplayNameForStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import {
  isInitialStep,
  uiStepAllComponentConfigs,
  uiStepAllNonVirtualComponentsAndConfig,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import ScrollablePanelWithFooter from 'dashboard/components/ScrollablePanelWithFooter';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { RootState } from 'dashboard/redux/store';
import { refreshComponentPreview } from 'dashboard/redux/thunk-actions/inquiry-template';
import Badge from 'design-system/components/Badge/Badge';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { Paragraph } from 'design-system/components/Text';
import { fadeIn } from 'design-system/variables/keyframes';
import { TransitionDuration } from 'design-system/variables/transitions';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { StepUi } from 'lib/interfaces/StepsConfig';
import {
  FieldKey,
  InputAddressComponentConfig,
  InputInternationalDbComponentConfig,
  isImageComponentConfig,
  isTextComponent,
  isTextComponentConfig,
  isUiFormComponentConfig,
  isUiFormFieldComponentConfig,
  isUiFormMultiFieldComponentConfig,
  isUiParentComponentConfig,
  StepperStepConfig,
  UiComponentConfig,
  UiComponentConfigType,
  UiFormComponentConfig,
  UiImageComponentConfig,
} from 'lib/interfaces/ui-components';

const getTitleAndFieldKeyValue = <C extends UiFormComponentConfig = UiFormComponentConfig>(
  componentConfig: C,
  fieldKey: FieldKey<C>,
): { title: string | undefined; fieldKeyValue: string | null } => {
  if (isUiFormMultiFieldComponentConfig(componentConfig)) {
    let title;
    let fieldKeyValue;
    switch (componentConfig.type) {
      case UiComponentConfigType.InputAddress: {
        const currentFieldKey = fieldKey as FieldKey<InputAddressComponentConfig>;
        title = COMPONENT_FIELD_DEFAULTS[componentConfig.type][currentFieldKey].name;
        fieldKeyValue = componentConfig.config[currentFieldKey];
        break;
      }
      case UiComponentConfigType.InputInternationalDb: {
        const currentFieldKey = fieldKey as FieldKey<InputInternationalDbComponentConfig>;
        title = COMPONENT_FIELD_DEFAULTS[componentConfig.type][currentFieldKey].name;
        fieldKeyValue = componentConfig.config[currentFieldKey];
        break;
      }
    }
    return {
      title,
      fieldKeyValue,
    };
  } else {
    return { title: undefined, fieldKeyValue: componentConfig.config.field };
  }
};

const StyledTabBar = styled(TabBar)`
  margin-bottom: -${Spacing[5]};
  border-bottom: 0;
  padding-top: ${Spacing[5]};
  height: auto;
  &:after {
    display: none;
  }
`;

const InquiryTemplateEditorComponentPanel = styled.div`
  overflow: hidden;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
`;

const UnsupportedTextContainer = styled.div`
  padding: ${Spacing[4]} ${Spacing[5]};
`;

const StyledCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  animation: ${fadeIn} ${TransitionDuration.Medium} ease-in-out;
  padding: 6px;
  margin: 0;
`;

export type ComponentFieldReference = {
  componentName: string;
  stepName: string;
  displayLabel: string;
};

/**
 * Get all components which reference the current component's
 * referenced field to explicitly surface within FormComponentFieldsEditor
 */
const getReferencingComponentsForField = (
  fieldKey: string | null,
  steps: InquiryTemplateVersionStep[],
): ComponentFieldReference[] =>
  steps.filter(isUiStepConfig).flatMap((step) =>
    uiStepAllComponentConfigs(step)
      .filter(
        (componentConfig) =>
          isUiFormFieldComponentConfig(componentConfig) &&
          componentConfig.config.field === fieldKey,
      )
      .map((componentConfig) => ({
        componentName: componentConfig.name,
        stepName: step.name,
        displayLabel: `${componentConfig.name} (Step: ${getDisplayNameForStep(step)})`,
      })),
  );

type ComponentPanelProps = {
  selectedComponentAndStep: SelectedComponentAndStep;
  clientData: StepUi;
  basePath: string;
  baseRoutePath: string;
  currentStep: InquiryTemplateVersionStepUi;
  enabledLocales: InquiryIso6391LanguageCode[];
  currentLocale: InquiryIso6391LanguageCode;
  steps: InquiryTemplateVersionStep[];
  workflowVersionMap: WorkflowVersionMap;
  submissionComponentErrors: ComponentValidationErrorsMap | undefined;
  fieldSchema: FieldSchema[];
  theme: ThemeAttributes;
  editable: boolean;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  handleClosePanel: () => void;
  handleComponentDelete: (componentSource?: SelectedComponentAndStep) => void;
  handleComponentCopy?: (displaySuccessToast: boolean) => void;
  handleComponentPaste?: () => void;
  setSelectedComponentAndStep: (selection: SelectedComponentAndStep) => void;
  updateSteps: ({
    updatedStep,
    component,
  }: {
    updatedStep: InquiryTemplateVersionStepUi;
    component?: UiComponentConfig;
  }) => void;
};

const ComponentPanel = ({
  selectedComponentAndStep,
  clientData,
  basePath,
  baseRoutePath,
  currentStep,
  enabledLocales,
  currentLocale,
  steps,
  workflowVersionMap,
  submissionComponentErrors,
  fieldSchema,
  theme,
  editable,
  getStepLocalizedStrings,
  handleClosePanel,
  handleComponentDelete,
  handleComponentCopy,
  handleComponentPaste,
  setSelectedComponentAndStep,
  updateSteps,
}: ComponentPanelProps) => {
  const dispatch = useDispatch();

  const [copyConfirmationActive, { enable: showConfirmation, disable: hideConfirmation }] =
    useToggle(false);

  const componentPreviewMap = useMemo(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { stepUiComponents: uiComponentsNonVirtual, stepUiComponentConfigs: uiComponentConfigs } =
      uiStepAllNonVirtualComponentsAndConfig({
        step: clientData,
        stepConfig: currentStep,
      });

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const previewIndex = uiComponentConfigs.findIndex((componentConfig) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return componentConfig.name === selectedComponentAndStep.component.name;
    });

    const componentPath = findComponentConfigPath(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (componentConfig) => componentConfig.name === selectedComponentAndStep.component.name,
      currentStep.config.components,
    );

    return {
      preview: uiComponentsNonVirtual[previewIndex],
      path: componentPath,
    };
  }, [selectedComponentAndStep, currentStep, clientData]);

  const handleImageSelect = useCallback(
    (asset: Asset, imageSetting: ComponentSettingImage) => {
      if (!componentPreviewMap.path) return;
      const componentPath = componentPreviewMap.path.join('.');
      const selectedImageComponentConfig = get(componentPath, currentStep.config.components);

      const updatedImageComponent = getAssetUpdatedImageConfig({
        asset,
        componentConfig: selectedImageComponentConfig,
        imageSetting,
        theme: theme,
      }) as UiImageComponentConfig;

      dispatch(refreshComponentPreview(currentStep.name, componentPath, updatedImageComponent));

      const updatedStep = set(
        `config.components.${componentPath}`,
        updatedImageComponent,
        currentStep,
      );
      updateSteps({ updatedStep, component: updatedImageComponent });
    },
    [componentPreviewMap.path, currentStep, theme, updateSteps, dispatch],
  );

  const isComponentWithoutProperties = COMPONENTS_WITHOUT_PROPERTY_SUPPORT.includes(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    selectedComponentAndStep.component.type,
  );

  const isComponentWithOnlyJsonLogicProperties = COMPONENTS_WITH_ONLY_JSON_LOGIC.includes(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    selectedComponentAndStep.component.type,
  );

  const isRestrictedComponent = RESTRICTED_COMPONENTS.includes(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    selectedComponentAndStep.component.type,
  );

  const isBiometricSubmitButton = isCurrentComponentBIPASubmitTrigger({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    step: selectedComponentAndStep.step,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    component: selectedComponentAndStep.component,
  });

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const isParentComponent = isUiParentComponentConfig(selectedComponentAndStep.component);

  const initialInquiryTemplateVersion = useSelector<RootState, InquiryTemplateVersion | undefined>(
    (state) => state.inquiryTemplate?.inquiryTemplateTreeData?.inquiryTemplateVersion,
  );

  // component must be new if base steps config form values does not contain either:
  // 1) the current step entirely (new step added from tree with some components)
  // 2) the current component within the step (new components added to existing step)
  const isNewComponent =
    getCurrentComponent(
      initialInquiryTemplateVersion?.attributes?.steps ?? [],
      selectedComponentAndStep,
    ) == null;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const isFormFieldComponentConfig = isUiFormComponentConfig(selectedComponentAndStep.component);

  const enableValidationsEditor = useFeatureFlag(
    'dashboard:fle-enable-screen-editor-validations-editor',
  );

  // Note: (Evan) Screen can become temporarily desynced as new steps config are added and stepPreview is updating
  const desynced = useMemo(() => componentPreviewMap.path == null, [componentPreviewMap.path]);

  // We need to use the component from the currentStep, rather than from selectedComponentAndStep,
  // which isn't updated from form field updates
  const currentComponent: UiComponentConfig | undefined = useMemo(
    () =>
      componentPreviewMap.path
        ? get(componentPreviewMap.path, currentStep.config.components)
        : undefined,
    [componentPreviewMap.path, currentStep],
  );

  const selectedEditorComponentSchema =
    currentComponent?.type &&
    (EDITOR_COMPONENTS_SCHEMA[currentComponent.type] as EditorComponentSchema<
      typeof currentComponent
    >);

  const canAccessLogicTab = (selectedEditorComponentSchema?.logic.length || 0) > 0;

  const joinedComponentConfigPath = componentPreviewMap.path?.join('.');

  const componentBasePath = `${basePath}.config.components.${joinedComponentConfigPath}`;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const componentValidationErrors = submissionComponentErrors?.[joinedComponentConfigPath]?.filter(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ({ stepName }) => stepName === selectedComponentAndStep.step.name,
  );

  const handleUpdateFormConfig = useCallback(
    (componentConfigUpdates: Record<string, any>) => {
      if (componentPreviewMap.path) {
        const updatedStep = generateUpdatedStepFromComponentUpdates(
          currentStep,
          componentPreviewMap.path,
          componentConfigUpdates,
        );

        const componentPath = componentPreviewMap.path.join('.');
        const updatedComponent = get(`config.components.${componentPath}`, updatedStep);

        if (
          updatedComponent &&
          initialInquiryTemplateVersion &&
          intersection(
            EDITOR_COMPONENTS_SCHEMA[(updatedComponent as UiComponentConfig).type]?.properties[
              'refresh-preview-attributes'
            ],
            Object.keys(componentConfigUpdates).map((key) => key.split('.')[0]),
          ).length > 0
        ) {
          dispatch(refreshComponentPreview(updatedStep.name, componentPath, updatedComponent));
        }
        updateSteps({
          updatedStep,
        });
      }
    },
    [componentPreviewMap.path, currentStep, updateSteps, initialInquiryTemplateVersion, dispatch],
  );

  // some components can be modified via component panel but not via library
  // The required access tier derived from the component schema is at most the same tier as componentAccessTier or lower
  const componentAccessTier =
    selectedEditorComponentSchema?.['required-access-tier'] ??
    (selectedComponentAndStep && ComponentAccessTiers[selectedComponentAndStep.component.type]);

  const canEditComponentAtAccessLevel = useIsEditableWithOptions({
    accessTier: componentAccessTier ?? undefined,
  });

  const hasAccessForTier = useHasAccessForTierDeterminator();

  const disableImageComponent =
    currentComponent &&
    componentSelfServeUiConditions({
      hasFreeTierAccess: hasAccessForTier(EditorAccessTier.InquiriesFree),
      componentConfig: currentComponent,
      isInitialStep: isInitialStep(currentStep),
      isAccessibleForBasic: hasAccessForTier(EditorAccessTier.InquiriesBasic),
    }).disableImageComponent;

  let sticker = null;

  const isAccessibleAtTier = hasAccessForTier(componentAccessTier ?? undefined);
  if (disableImageComponent || !isAccessibleAtTier) {
    sticker = (
      <InquiriesGatingSticker
        dataMarketingIdSuffix={`${currentComponent?.type}-config`}
        stickerType={EnhancedGatingStickerType.Limited}
      />
    );
  }

  let isEditableWithAccess = editable && canEditComponentAtAccessLevel;

  if (isImageComponentConfig(currentComponent)) {
    isEditableWithAccess = isEditableWithAccess && !disableImageComponent;
  }

  const componentScreenEditorData =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ScreenEditorComponentConfigTypes[selectedComponentAndStep.component.type];

  const componentPanelFallback = getComponentPanelFallback({
    selectedComponentAndStep,
    componentScreenEditorData,
  });

  const componentJsonLogicFields = useMemo(
    () =>
      canAccessLogicTab && selectedEditorComponentSchema && currentComponent
        ? selectedEditorComponentSchema.logic.reduce<ComponentJsonLogicRecord>(
            (jsonKeyRecord, jsonLogicConfig) => {
              if (jsonLogicConfig.namespace && currentComponent.config) {
                // map over nested configuration to extract the corresponding rules logic
                (
                  currentComponent.config[
                    jsonLogicConfig.namespace as keyof (typeof currentComponent)['config']
                  ] as StepperStepConfig[]
                ).map((config, configIdx) => {
                  jsonKeyRecord[
                    `${jsonLogicConfig.namespace}.${configIdx}.${jsonLogicConfig['client-key']}`
                  ] = {
                    logic: config[jsonLogicConfig['client-key'] as StepperStepLogicKeys],
                    ruleName: jsonLogicConfig['rule-name'],
                    invertedRuleName: jsonLogicConfig['inverted-rule-name'],
                    section: jsonLogicConfig['evaluation-context'],
                  };
                });
              } else {
                jsonKeyRecord[jsonLogicConfig['client-key']] = {
                  logic:
                    currentComponent.config &&
                    currentComponent.config[
                      jsonLogicConfig['client-key'] as keyof (typeof currentComponent)['config']
                    ],
                  ruleName: jsonLogicConfig['rule-name'],
                  invertedRuleName: jsonLogicConfig['inverted-rule-name'],
                  section: jsonLogicConfig['evaluation-context'],
                };
              }

              return jsonKeyRecord;
            },
            {} as ComponentJsonLogicRecord,
          )
        : {},
    [canAccessLogicTab, selectedEditorComponentSchema, currentComponent],
  ) as ComponentJsonLogicRecord;

  const { totalCount: totalJsonConfigFieldsWithRulesLogic } =
    componentTotalNumConditionalConfigsWithRulesLogic(currentComponent || null);

  const onDeleteComponentButtonClick = () => {
    handleComponentDelete();
  };

  const { componentPanelSettings, setComponentPanelSettings, tabSettingID } =
    useComponentEditorComponentPanelSettings({
      selectedComponentAndStep,
      canAccessLogicTab,
      isNewComponent,
    });

  if (desynced) {
    return <></>;
  }

  // Note (Evan): if data for component config type is not defined in ScreenEditorComponentConfigTypes,
  // show an alternate version of the panel highlighting the lack of support in editor
  if (componentPanelFallback?.type === ComponentPanelFallbackCondition.ComponentLibrary) {
    return (
      <InquiryTemplateEditorComponentPanel>
        <PanelHeaderSection>{componentPanelFallback?.screen}</PanelHeaderSection>
      </InquiryTemplateEditorComponentPanel>
    );
  }

  const footerContent = (
    <>
      <Button
        variant='basic'
        width='auto'
        id='component-panel__close-button'
        onClick={handleClosePanel}
      >
        Close
      </Button>
      {isEditableWithAccess && !isRestrictedComponent && (
        <>
          <Button
            title='Delete'
            width='icon'
            variant='basic'
            id='component-panel__delete-button'
            onClick={onDeleteComponentButtonClick}
          >
            <TrashIcon />
          </Button>
          {handleComponentCopy && (
            <Popover
              open={copyConfirmationActive}
              trigger={
                <Button
                  title='Copy'
                  width='icon'
                  variant='basic'
                  id='component-panel__copy-button'
                  onClick={() => {
                    handleComponentCopy(false);
                    showConfirmation();
                    setTimeout(() => {
                      hideConfirmation();
                    }, 1200);
                  }}
                >
                  <CopyIcon />
                </Button>
              }
            >
              <Popover.Content side='top' align='center' style={{ zIndex: 2 }}>
                <StyledCard elevated>
                  <Paragraph>Copied!</Paragraph>
                </StyledCard>
              </Popover.Content>
            </Popover>
          )}
          {isParentComponent && handleComponentPaste && (
            <Button
              title='Paste'
              width='icon'
              variant='basic'
              id='component-panel__paste-button'
              onClick={handleComponentPaste}
            >
              <ClipboardIcon />
            </Button>
          )}
        </>
      )}
    </>
  );

  return (
    <InquiryTemplateEditorComponentPanel>
      <PanelHeaderSection>
        <PanelHeaderTitle
          title={componentScreenEditorData.displayName}
          subtitle='Component'
          editable={false}
          sticker={sticker}
          copyText={currentComponent?.name}
        />
        {!isRestrictedComponent && (
          <StyledTabBar>
            <Tab
              id='ite-component-control-panel__container__tab-properties'
              selected={componentPanelSettings.tab === SelectedComponentTab.PROPERTIES}
              onClick={() =>
                setComponentPanelSettings({
                  tab: SelectedComponentTab.PROPERTIES,
                  panelOpenForNewComponent: false,
                  id: tabSettingID,
                })
              }
            >
              Properties
            </Tab>

            <Tab
              id='ite-component-control-panel__container__tab-styles'
              selected={componentPanelSettings.tab === SelectedComponentTab.STYLES}
              onClick={() =>
                setComponentPanelSettings({
                  tab: SelectedComponentTab.STYLES,
                  panelOpenForNewComponent: false,
                  id: tabSettingID,
                })
              }
            >
              Styles
            </Tab>
            {isFormFieldComponentConfig && (
              <Tab
                id='ite-component-control-panel__container__tab-field'
                selected={componentPanelSettings.tab === SelectedComponentTab.FIELD}
                onClick={() =>
                  setComponentPanelSettings({
                    tab: SelectedComponentTab.FIELD,
                    panelOpenForNewComponent: false,
                    id: tabSettingID,
                  })
                }
              >
                Fields
              </Tab>
            )}
            {canAccessLogicTab && (
              <Tab
                id='ite-component-panel__container__tab-logic'
                selected={componentPanelSettings.tab === SelectedComponentTab.LOGIC}
                onClick={() =>
                  setComponentPanelSettings({
                    tab: SelectedComponentTab.LOGIC,
                    panelOpenForNewComponent: false,
                    id: tabSettingID,
                  })
                }
                {...(totalJsonConfigFieldsWithRulesLogic > 0 && {
                  badge: <Badge>{totalJsonConfigFieldsWithRulesLogic}</Badge>,
                })}
              >
                Logic
              </Tab>
            )}
          </StyledTabBar>
        )}
      </PanelHeaderSection>
      <ScrollablePanelWithFooter footerContent={footerContent}>
        {isRestrictedComponent && (
          <UnsupportedTextContainer>
            <Paragraph color='secondary' size='sm'>
              This component cannot be directly edited
            </Paragraph>
          </UnsupportedTextContainer>
        )}

        {!isRestrictedComponent && (
          <>
            {componentPanelSettings.tab === SelectedComponentTab.STYLES && (
              <FormSection component={Fragment} name='steps'>
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                {STYLEABLE_COMPONENTS.includes(selectedComponentAndStep.component.type) &&
                  currentComponent && (
                    <ComponentEditorStyles
                      stepPreview={clientData}
                      component={currentComponent}
                      componentPreview={componentPreviewMap.preview}
                      componentBasePath={componentBasePath}
                      editable={isEditableWithAccess}
                    />
                  )}
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                {!STYLEABLE_COMPONENTS.includes(selectedComponentAndStep.component.type) && (
                  <UnsupportedTextContainer>
                    <Paragraph color='secondary' size='sm'>
                      Styles support for this component coming soon!
                    </Paragraph>
                  </UnsupportedTextContainer>
                )}
              </FormSection>
            )}
            {componentPanelSettings.tab === SelectedComponentTab.PROPERTIES && (
              <FormSection component={Fragment} name='steps'>
                {(isComponentWithoutProperties ||
                  (isComponentWithOnlyJsonLogicProperties && !canAccessLogicTab)) && (
                  <UnsupportedTextContainer>
                    <Paragraph color='secondary' size='sm'>
                      This component has no configurable properties.
                    </Paragraph>
                  </UnsupportedTextContainer>
                )}
                {(!isComponentWithoutProperties ||
                  (isComponentWithOnlyJsonLogicProperties && canAccessLogicTab)) && (
                  <>
                    {selectedEditorComponentSchema &&
                      selectedEditorComponentSchema?.properties.settings && (
                        <ComponentConfigSettingAddOns
                          stepBasePath={basePath}
                          basePath={componentBasePath}
                          componentConfig={currentComponent}
                          editable={isEditableWithAccess}
                          isNewComponent={isNewComponent}
                          baseRoutePath={baseRoutePath}
                          currentStep={currentStep}
                        >
                          {selectedEditorComponentSchema.properties.settings.map((setting, idx) => (
                            <ComponentConfigSetting
                              // key must contain component name as unique identifier to ensure that
                              // stale values aren't rendered when switching between components
                              key={`${currentComponent.name}-${idx}`}
                              basePath={componentBasePath}
                              componentSetting={setting}
                              editable={isEditableWithAccess}
                              componentConfig={currentComponent.config}
                              componentType={currentComponent.type}
                              componentPreview={componentPreviewMap.preview}
                              stepName={clientData.name}
                              enabledLocales={enabledLocales}
                              currentLocale={currentLocale}
                              workflowVersionMap={workflowVersionMap}
                              steps={steps}
                              fieldSchema={fieldSchema}
                              isNewComponent={isNewComponent}
                              componentValidationErrors={componentValidationErrors}
                              currentStep={currentStep}
                              getStepLocalizedStrings={getStepLocalizedStrings}
                              handleUpdateComponentConfig={handleUpdateFormConfig}
                              handleImageSelect={handleImageSelect}
                            />
                          ))}
                        </ComponentConfigSettingAddOns>
                      )}
                    {isUiFormFieldComponentConfig(currentComponent) &&
                      'validations' in currentComponent.config &&
                      enableValidationsEditor && (
                        <ValidationsEditor
                          componentConfig={currentComponent}
                          fieldSchema={fieldSchema}
                          currentLocale={currentLocale}
                          enabledLocales={enabledLocales}
                          editable={editable}
                          handleValidationsUpdate={handleUpdateFormConfig}
                        />
                      )}
                    {selectedComponentAndStep &&
                      isTextComponentConfig(selectedComponentAndStep.component) &&
                      isTextComponent(componentPreviewMap.preview) && (
                        <TranslationsEditor
                          basePath={componentBasePath}
                          componentName={selectedComponentAndStep.component.name}
                          componentPreview={componentPreviewMap.preview}
                          stepName={clientData.name}
                          enabledLocales={enabledLocales}
                          translationsSchema={
                            selectedEditorComponentSchema?.properties.translations ?? []
                          }
                          currentLocale={currentLocale}
                          handleTranslationUpdates={handleUpdateFormConfig}
                          getStepLocalizedStrings={getStepLocalizedStrings}
                          editable={editable && !isBiometricSubmitButton}
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          disabledWarning={
                            isBiometricSubmitButton
                              ? 'Cannot edit text due to BIPA compliance'
                              : null
                          }
                        />
                      )}
                  </>
                )}
              </FormSection>
            )}
            {componentPanelSettings.tab === SelectedComponentTab.FIELD &&
              isUiFormComponentConfig(currentComponent) && (
                <>
                  {componentPanelFallback?.type === ComponentPanelFallbackCondition.FieldSchema &&
                    componentPanelFallback?.screen}
                  {componentPanelFallback?.type !== ComponentPanelFallbackCondition.FieldSchema && (
                    <SectionAccordion
                      title={
                        isUiFormMultiFieldComponentConfig(currentComponent) ? 'Fields' : 'Field'
                      }
                    >
                      <FormComponentOptionalEditor
                        componentConfig={currentComponent}
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        stepConfig={selectedComponentAndStep.step}
                        editable={isEditableWithAccess}
                        handleUpdateComponentConfig={handleUpdateFormConfig}
                      />
                      {getComponentFieldKeys(currentComponent).map((fieldKey, index) => {
                        // return title for components with multiple fields like InputAddress
                        // in order to distinguish separate field sub-sections in the panel
                        const { title, fieldKeyValue } = getTitleAndFieldKeyValue(
                          currentComponent,
                          fieldKey,
                        );
                        const initialFieldSchema =
                          initialInquiryTemplateVersion?.attributes?.['field-schema'] ?? [];
                        const isNewField =
                          initialFieldSchema.find((field) => field.key === fieldKeyValue) == null;
                        return (
                          <FormComponentFieldsEditor
                            key={fieldKey}
                            title={title}
                            fieldKey={fieldKey}
                            componentReferences={getReferencingComponentsForField(
                              fieldKeyValue,
                              steps,
                            )}
                            currentLocale={currentLocale}
                            editable={isEditableWithAccess}
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            step={selectedComponentAndStep.step}
                            basePath={componentBasePath}
                            component={currentComponent}
                            isNewField={isNewField}
                            fieldSchema={fieldSchema}
                            offsetElementId={SCREEN_EDITOR_CONTROL_PANEL_ID}
                            autoFocus={index === 0}
                            handleUpdateComponentConfig={handleUpdateFormConfig}
                            setSelectedComponentAndStep={setSelectedComponentAndStep}
                          />
                        );
                      })}
                    </SectionAccordion>
                  )}
                </>
              )}
            {componentPanelSettings.tab === SelectedComponentTab.LOGIC &&
              canAccessLogicTab &&
              currentComponent && (
                <ComponentJsonLogicEditor
                  step={currentStep}
                  componentConfig={currentComponent}
                  jsonLogicFields={componentJsonLogicFields}
                  editable={editable}
                  onJSONUpdate={handleUpdateFormConfig}
                />
              )}
          </>
        )}
      </ScrollablePanelWithFooter>
    </InquiryTemplateEditorComponentPanel>
  );
};

export default memo(ComponentPanel);
